import { logDataDiag } from "@/lib/dataDiagnostics";
import {
  FetchTimeoutError,
  InvalidJsonResponseError,
  fetchJsonWithTimeout,
} from "@/lib/fetchWithTimeout";

const SAFE_METHODS = new Set(["GET", "HEAD"]);
const RETRYABLE_STATUSES = new Set([408, 425, 429, 502, 503, 504]);

export class ClientRequestError extends Error {
  constructor(message, details = {}) {
    const cause = details.payload || details.cause || undefined;
    super(message, cause ? { cause } : undefined);
    this.name = "ClientRequestError";
    this.kind = details.kind || "unknown";
    this.status = details.status ?? null;
    this.code = details.code || null;
    this.payload = details.payload || null;
    if (cause && !this.cause) this.cause = cause;
    this.retryable = details.retryable === true;
    this.attempts = details.attempts || 1;
  }
}

function isCallerAbort(error, signal) {
  return Boolean(signal?.aborted) || error?.name === "AbortError";
}

function networkKind() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }
  return "network";
}

function diagnosticRequestTarget(url) {
  try {
    const pathname = new URL(String(url), "https://diagnostic.invalid").pathname;
    return pathname
      .replace(/\/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?=\/|$)/gi, "/:id")
      .replace(/\/[A-Za-z0-9_-]{24,}(?=\/|$)/g, "/:token");
  } catch {
    return "request";
  }
}

function errorForResponse(response, payload, attempts) {
  const status = response.status;
  const kind =
    status === 401
      ? "session_expired"
      : status === 403
        ? "forbidden"
        : status >= 500
          ? "server"
          : status === 408
            ? "timeout"
            : "http";
  const message =
    payload?.error ||
    payload?.message ||
    (status === 401
      ? "Your session has expired."
      : status === 403
        ? "You do not have permission to view this."
        : status >= 500
          ? "The server could not complete the request."
          : "The request could not be completed.");

  return new ClientRequestError(message, {
    kind,
    status,
    code: payload?.code || null,
    payload,
    retryable: RETRYABLE_STATUSES.has(status),
    attempts,
  });
}

function errorForThrown(error, { signal, attempts }) {
  if (error instanceof ClientRequestError) return error;
  if (error instanceof FetchTimeoutError || error?.name === "TimeoutError") {
    return new ClientRequestError("The request took too long.", {
      kind: "timeout",
      code: "REQUEST_TIMEOUT",
      retryable: true,
      attempts,
      cause: error,
    });
  }
  if (error instanceof InvalidJsonResponseError || error?.code === "INVALID_JSON_RESPONSE") {
    return new ClientRequestError("The server returned an invalid response.", {
      kind: "server",
      status: error?.status ?? null,
      code: "INVALID_JSON_RESPONSE",
      retryable: true,
      attempts,
      cause: error,
    });
  }
  if (isCallerAbort(error, signal)) {
    return new ClientRequestError("The request was canceled.", {
      kind: "aborted",
      code: "REQUEST_ABORTED",
      retryable: false,
      attempts,
      cause: error,
    });
  }
  const kind = networkKind();
  return new ClientRequestError(
    kind === "offline" ? "You appear to be offline." : "The network request failed.",
    {
      kind,
      code: kind === "offline" ? "OFFLINE" : "NETWORK_ERROR",
      retryable: true,
      attempts,
      cause: error,
    }
  );
}

function waitForRetry(delayMs, signal) {
  if (!delayMs) return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason || new DOMException("Aborted", "AbortError"));
      return;
    }
    const finish = () => {
      signal?.removeEventListener?.("abort", handleAbort);
      resolve();
    };
    const timer = setTimeout(finish, delayMs);
    const handleAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener?.("abort", handleAbort);
      reject(signal.reason || new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener?.("abort", handleAbort, { once: true });
  });
}

export async function requestJson(url, options = {}) {
  const {
    timeoutMs = 8000,
    retries,
    retryDelayMs = 200,
    signal,
    ...fetchOptions
  } = options;
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const maxRetries = SAFE_METHODS.has(method)
    ? Math.max(0, Number.isInteger(retries) ? retries : 1)
    : 0;
  const startedAt = Date.now();
  const diagnosticTarget = diagnosticRequestTarget(url);
  let attempt = 0;

  while (attempt <= maxRetries) {
    attempt += 1;
    try {
      const { response, payload } = await fetchJsonWithTimeout(url, {
        ...fetchOptions,
        signal,
        timeoutMs,
      });
      if (!response.ok) {
        throw errorForResponse(response, payload, attempt);
      }
      logDataDiag("request:complete", {
        target: diagnosticTarget,
        method,
        status: response.status,
        durationMs: Date.now() - startedAt,
        attempts: attempt,
      });
      return { response, payload, attempts: attempt };
    } catch (error) {
      const requestError = errorForThrown(error, { signal, attempts: attempt });
      const canRetry =
        attempt <= maxRetries &&
        requestError.retryable &&
        requestError.kind !== "offline" &&
        requestError.kind !== "session_expired" &&
        requestError.kind !== "forbidden";
      logDataDiag(canRetry ? "request:retry" : "request:failed", {
        target: diagnosticTarget,
        method,
        kind: requestError.kind,
        status: requestError.status,
        durationMs: Date.now() - startedAt,
        attempts: attempt,
      });
      if (!canRetry) throw requestError;
      try {
        await waitForRetry(retryDelayMs * attempt, signal);
      } catch (retryWaitError) {
        throw errorForThrown(retryWaitError, { signal, attempts: attempt });
      }
    }
  }

  throw new ClientRequestError("The request could not be completed.");
}

export function requestErrorMessage(error, fallback = "The request could not be completed.") {
  switch (error?.kind) {
    case "offline":
      return "You are offline. Reconnect and try again.";
    case "timeout":
      return "This is taking longer than expected. Try again.";
    case "session_expired":
      return "Your session expired. Sign in again, then retry.";
    case "forbidden":
      return "You do not have permission to view this.";
    case "server":
      return "YourBarrio could not load this right now. Try again.";
    case "network":
      return "The network request failed. Check your connection and try again.";
    default:
      return error?.message || fallback;
  }
}
